<script setup lang="ts">
import { onMounted, ref } from 'vue';
import DxTabPanel, { DxItem as DxTabItem } from 'devextreme-vue/tab-panel';
import DxForm, { DxSimpleItem, DxLabel } from 'devextreme-vue/form';
import { DxButton } from 'devextreme-vue/button';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import {
  BottoPagePanel,
  LookupBox,
  getAggregatorsLookup,
  getStreamsLookup,
  showError,
} from '@botto/core';
import {
  fetchLinkClicks,
  fetchLinkReplacements,
  fetchLinkStats,
  type LinkReplacementsQuery,
} from '../../api';
import DynamicResultGrid from '../../components/DynamicResultGrid.vue';

type LinksTabKey = 'links' | 'stats' | 'clicks';

interface TabState {
  filters: {
    start_date: Date;
    end_date: Date;
    stream_id: number | null;
    aggregator_id: number | null;
  };
  result: unknown;
  loaded: boolean;
}

function defaultRange(): { start_date: Date; end_date: Date } {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 7);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 0);
  return { start_date: start, end_date: end };
}

function formatApiDateTime(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  const d = value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function createTabState(): TabState {
  const range = defaultRange();
  return {
    filters: {
      start_date: range.start_date,
      end_date: range.end_date,
      stream_id: null,
      aggregator_id: null,
    },
    result: null,
    loaded: false,
  };
}

const streamsLookup = getStreamsLookup();
const aggregatorsLookup = getAggregatorsLookup();
const loading = ref(false);
const selectedTab = ref(0);

const tabs = ref<Record<LinksTabKey, TabState>>({
  links: createTabState(),
  stats: createTabState(),
  clicks: createTabState(),
});

const TAB_META: { key: LinksTabKey; title: string }[] = [
  { key: 'links', title: 'Ссылки' },
  { key: 'stats', title: 'Статистика' },
  { key: 'clicks', title: 'Клики' },
];

function setFilter<K extends keyof TabState['filters']>(
  tab: LinksTabKey,
  field: K,
  value: TabState['filters'][K],
) {
  tabs.value[tab].filters[field] = value;
}

function buildQuery(tab: LinksTabKey): LinkReplacementsQuery | null {
  const f = tabs.value[tab].filters;
  const start_date = formatApiDateTime(f.start_date);
  const end_date = formatApiDateTime(f.end_date);
  if (!start_date || !end_date) {
    showError(new Error('Укажите начало и конец диапазона'));
    return null;
  }
  return {
    start_date,
    end_date,
    stream_id: f.stream_id,
    aggregator_id: f.aggregator_id,
    offset: 0,
    limit: 1000,
  };
}

async function loadTab(tab: LinksTabKey) {
  const query = buildQuery(tab);
  if (!query) return;

  loading.value = true;
  tabs.value[tab].result = null;
  try {
    const fetcher =
      tab === 'links' ? fetchLinkReplacements : tab === 'stats' ? fetchLinkStats : fetchLinkClicks;
    tabs.value[tab].result = await fetcher(query);
    tabs.value[tab].loaded = true;
  } catch (e) {
    showError(e);
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  void streamsLookup.ensureLoaded();
  void aggregatorsLookup.ensureLoaded();
});
</script>

<template>
  <BottoPagePanel title="Ссылки" standalone>
    <DxTabPanel
      class="links-tabs"
      v-model:selected-index="selectedTab"
      :animation-enabled="true"
      :swipe-enabled="false"
      :defer-rendering="false"
    >
      <DxTabItem v-for="(tab, index) in TAB_META" :key="tab.key" :title="tab.title">
        <div class="links-pane">
          <DxForm :form-data="tabs[tab.key].filters" :col-count="4" label-location="top" class="links-filters">
            <DxSimpleItem
              data-field="start_date"
              editor-type="dxDateBox"
              :editor-options="{
                type: 'datetime',
                displayFormat: 'dd.MM.yyyy HH:mm',
                dateSerializationFormat: 'yyyy-MM-ddTHH:mm:ss',
                width: '100%',
              }"
            >
              <DxLabel text="Начало" />
            </DxSimpleItem>
            <DxSimpleItem
              data-field="end_date"
              editor-type="dxDateBox"
              :editor-options="{
                type: 'datetime',
                displayFormat: 'dd.MM.yyyy HH:mm',
                dateSerializationFormat: 'yyyy-MM-ddTHH:mm:ss',
                width: '100%',
              }"
            >
              <DxLabel text="Конец" />
            </DxSimpleItem>
            <DxSimpleItem>
              <DxLabel text="Стрим" />
              <LookupBox
                :model-value="tabs[tab.key].filters.stream_id"
                :lookup="streamsLookup"
                placeholder="Стрим"
                @update:model-value="(v) => setFilter(tab.key, 'stream_id', v as number | null)"
              />
            </DxSimpleItem>
            <DxSimpleItem>
              <DxLabel text="Агрегатор" />
              <LookupBox
                :model-value="tabs[tab.key].filters.aggregator_id"
                :lookup="aggregatorsLookup"
                placeholder="Агрегатор"
                @update:model-value="(v) => setFilter(tab.key, 'aggregator_id', v as number | null)"
              />
            </DxSimpleItem>
          </DxForm>

          <div class="links-pane__actions">
            <DxButton
              text="Загрузить"
              type="default"
              styling-mode="contained"
              :disabled="loading"
              @click="loadTab(tab.key)"
            />
          </div>

          <div class="links-pane__result">
            <DynamicResultGrid
              v-if="tabs[tab.key].loaded || tabs[tab.key].result != null"
              :data="tabs[tab.key].result"
              :loading="loading && selectedTab === index"
            />
            <p v-else class="links-pane__empty">Выберите период и нажмите «Загрузить»</p>
          </div>
        </div>
      </DxTabItem>
    </DxTabPanel>
  </BottoPagePanel>

  <DxLoadPanel :visible="loading" :show-indicator="true" :shading="true" />
</template>

<style scoped>
.links-tabs {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.links-tabs :deep(.dx-tabpanel-container),
.links-tabs :deep(.dx-multiview-wrapper),
.links-tabs :deep(.dx-multiview-item) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

.links-tabs :deep(.dx-multiview-item-content) {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  padding: 0;
  overflow: hidden;
}

.links-pane {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  min-height: 0;
  padding: 4px 0 8px;
}

.links-filters {
  flex: 0 0 auto;
}

.links-pane__actions {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
}

.links-pane__result {
  flex: 1 1 auto;
  min-height: 280px;
  overflow: auto;
}

.links-pane__empty {
  margin: 12px 0 0;
  color: #888;
  font-size: 13px;
}
</style>
